import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import IconWithTooltip from "@/components/Chat/Messages/IconWithTooltip";
import { useAppSelector } from "@/hooks/redux";
import { selectConversations, selectCurrConversation } from "@/features/control/controlSlice";
import { selectRouterE2eeEnabled } from "@/features/auth/authSlice";
import { findGroupByAddress, redistributeGroupKey, requestGroupKey } from "@/services/groupKeyService";
import { refreshPublicKey } from "@/services/keyExchangeService";
import { isExternalDmAddress } from "@/utils/addresses";
import { KEY_SYNC_CONTACT_TOOLTIP, KEY_SYNC_GROUP_TOOLTIP, KEY_SYNC_OWNER_TOOLTIP } from "@/constants/ui";

/**
 * Manual key sync, next to the encryption shield: re-fetches the contact's key (1:1), asks the
 * owner for the group key again (group member), or re-sends the key to every member (group
 * owner). Covers what automatic sync can't — e.g. an Android owner that dropped a request, or
 * member requests that arrived while this owner was offline.
 */
const KeySyncButton = () => {
  const conversations = useAppSelector(selectConversations);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const routerE2eeEnabled = useAppSelector(selectRouterE2eeEnabled);
  const { userId, username } = useAppSelector((state) => state.auth);
  const conv = currConversationIndex !== null ? conversations[currConversationIndex] : null;
  const isGroup = conv?.type === "Group";
  const [groupOwner, setGroupOwner] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ownerName is absent after a reload (GetConversations doesn't return it) — use the groups list.
  useEffect(() => {
    setGroupOwner(null);
    if (!conv || !isGroup) return;
    if (conv.ownerName) { setGroupOwner(conv.ownerName); return; }
    let cancelled = false;
    findGroupByAddress(conv.address)
      .then((group) => { if (!cancelled) setGroupOwner(group?.ownerName ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [conv?.address, conv?.ownerName, isGroup]);

  if (!conv || !routerE2eeEnabled || !userId || !username) return null;
  if (isGroup ? !groupOwner : isExternalDmAddress(conv.address)) return null;
  const isOwner = isGroup && groupOwner === username;

  const handleSync = async () => {
    setBusy(true);
    try {
      if (isOwner) await redistributeGroupKey(conv.address, username, Number(userId));
      else if (isGroup) await requestGroupKey(conv.address, username, Number(userId), conv.groupName, true);
      else await refreshPublicKey(conv.address, Number(userId));
    } finally {
      setBusy(false);
    }
  };

  const label = isOwner ? KEY_SYNC_OWNER_TOOLTIP : isGroup ? KEY_SYNC_GROUP_TOOLTIP : KEY_SYNC_CONTACT_TOOLTIP;
  return (
    <IconWithTooltip label={label}>
      <Button variant="ghost" size="sm" className="w-8 px-0" onClick={handleSync} disabled={busy} aria-label={label}>
        <RefreshCw className={`h-4 w-4 text-neutral-500 ${busy ? "animate-spin" : ""}`} />
      </Button>
    </IconWithTooltip>
  );
};

export default KeySyncButton;
